import React, { useMemo } from 'react';
import { findInGameState } from '../actionHistoryManager';
import {
  collectEntityNamesFromLogPayload,
  collectEntityFactionColorByNameFromLogPayload,
  splitLineForUnitHighlights,
} from '../utils/battleActionHistoryI18n';
import {
  getFactionAccentColorForLogEntity,
  getBattleLogUnitNameHighlightStyle,
} from '../utils/factionUiColors';

/**
 * Action log snapshots often omit faction; resolve from live board state by instanceId.
 * @param {object} payload
 * @param {Record<string, string>} baseMap
 * @param {object | null | undefined} gameState
 * @returns {Record<string, string>}
 */
function mergeFactionColorsFromGameState(payload, baseMap, gameState) {
  if (!payload || !gameState?.state) return baseMap || {};
  const out = { ...(baseMap || {}) };
  const tryFill = (entity) => {
    if (!entity?.instanceId) return;
    const key = entity.name != null ? String(entity.name).trim() : '';
    if (!key || out[key]) return;
    const found = findInGameState(gameState, entity.instanceId);
    if (found?.order) {
      const hex = getFactionAccentColorForLogEntity(found.order);
      if (hex) out[key] = hex;
    }
  };
  tryFill(payload.unit);
  tryFill(payload.order);
  tryFill(payload.attacker);
  tryFill(payload.target);
  tryFill(payload.drone);
  tryFill(payload.weapon);
  tryFill(payload.strategem);
  tryFill(payload.source);
  tryFill(payload.killer);
  tryFill(payload.spawnedUnit);
  tryFill(payload.summonedUnit);
  tryFill(payload.killedUnit);
  tryFill(payload.actionItem);
  tryFill(payload.enhancer);
  if (Array.isArray(payload.targets)) {
    for (const u of payload.targets) {
      tryFill(u);
    }
  }
  return out;
}

/**
 * Renders one log line with entity names tinted by faction when known, else muted gray.
 * @param {{ payload: object, line: string, className?: string, gameState?: object }} p
 */
export default function LogTextWithUnitNames({ payload, line, className, gameState }) {
  const names = useMemo(() => collectEntityNamesFromLogPayload(payload), [payload]);
  const nameToColor = useMemo(() => {
    const base = collectEntityFactionColorByNameFromLogPayload(payload);
    return mergeFactionColorsFromGameState(payload, base, gameState);
  }, [payload, gameState]);
  const segments = useMemo(
    () => splitLineForUnitHighlights(String(line ?? ''), names, nameToColor),
    [line, names, nameToColor]
  );
  return (
    <span className={className}>
      {segments.map((seg, i) =>
        seg.isUnit ? (
          <span
            key={i}
            className="battle-action-log-unit-name"
            style={seg.color ? getBattleLogUnitNameHighlightStyle(seg.color) : undefined}
          >
            {seg.text}
          </span>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        )
      )}
    </span>
  );
}
